import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { Member, MemberStatus } from '@/types/members';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { MoreVertical, Edit, Trash2, Lock, Unlock, Shield, UserCheck } from 'lucide-react';

interface MemberTableDesktopProps {
  members: Member[];
  onEdit: (member: Member) => void;
  onResend: (member: Member) => void;
  onRemove: (member: Member) => void;
  onBlock?: (member: Member) => void;
  onUnblock?: (member: Member) => void;
}

export function MemberTableDesktop({ members, onEdit, onResend, onRemove, onBlock, onUnblock }: MemberTableDesktopProps) {
  useLanguage();
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getStatusLabel = (status: MemberStatus) => {
    const labels: Record<MemberStatus, string> = {
      'ativo': 'Ativo',
      'pendente': 'Pendente',
      'expirado': 'Bloqueado',
    };
    return labels[status] || status;
  };

  const getStatusBadgeClass = (status: MemberStatus) => {
    switch (status) {
      case 'ativo':
        return 'bg-[#dbe8ff] text-[#2a59d1] hover:bg-[#dbe8ff]';
      case 'pendente':
        return 'bg-[#ffe9d6] text-[#d88818] hover:bg-[#ffe9d6]';
      case 'expirado':
        return 'bg-[#ffe1e1] text-[#d34646] hover:bg-[#ffe1e1]';
      default:
        return '';
    }
  };

  const getAccessLabel = (accessType: string) => {
    return accessType === 'ADMIN' ? 'Administrador' : translateInterfaceText("Usuário Comum");
  };

  return (
    <div className="hidden md:block bg-white rounded-lg border border-[#E6EAF0] overflow-hidden">
      <div className="grid grid-cols-[1.5fr_1.5fr_1fr_1fr_1fr_auto] gap-4 px-6 py-3 bg-[#F8FAFC] border-b border-[#E6EAF0]">
        <div className="text-xs font-semibold text-[#64748B] uppercase tracking-wide">{translateInterfaceText("Nome")}</div>
        <div className="text-xs font-semibold text-[#64748B] uppercase tracking-wide">{translateInterfaceText("E-mail")}</div>
        <div className="text-xs font-semibold text-[#64748B] uppercase tracking-wide">{translateInterfaceText("Tipo Acesso")}</div>
        <div className="text-xs font-semibold text-[#64748B] uppercase tracking-wide">{translateInterfaceText("Status")}</div>
        <div className="text-xs font-semibold text-[#64748B] uppercase tracking-wide">{translateInterfaceText("Desde")}</div>
        <div className="text-xs font-semibold text-[#64748B] uppercase tracking-wide">{translateInterfaceText("Ações")}</div>
      </div>

      <div>
        {members.map((member) => (
          <div
            key={member.id}
            className="grid grid-cols-[1.5fr_1.5fr_1fr_1fr_1fr_auto] gap-4 px-6 py-4 border-b border-[#E6EAF0] last:border-b-0 hover:bg-[#FAFBFC] transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#E8F0FF] flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-semibold text-[#556ff8]">
                  {getInitials(member.name)}
                </span>
              </div>
              <span className="text-sm font-medium text-[#1E293B] truncate">
                {member.name}
              </span>
            </div>

            <div className="flex items-center">
              <span className="text-sm text-[#64748B] truncate">{member.email}</span>
            </div>

            <div className="flex items-center">
              <span className={`text-xs font-medium flex items-center gap-1 ${member.access_type === 'ADMIN' ? 'text-[#556ff8]' : 'text-[#64748B]'}`}>
                {member.access_type === 'ADMIN'
                  ? <Shield className="w-3.5 h-3.5" />
                  : <UserCheck className="w-3.5 h-3.5" />}
                {getAccessLabel(member.access_type)}
              </span>
            </div>

            <div className="flex items-center">
              <Badge className={`${getStatusBadgeClass(member.status)} text-xs font-medium`}>
                {getStatusLabel(member.status)}
              </Badge>
            </div>

            <div className="flex items-center">
              <span className="text-sm text-[#64748B]">{member.invited_at}</span>
            </div>

            <div className="flex items-center justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreVertical className="h-4 w-4 text-[#64748B]" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => onEdit(member)} className="cursor-pointer">
                    <Edit className="mr-2 h-4 w-4" />
                    {translateInterfaceText("Editar")}</DropdownMenuItem>
                  {member.status === 'ativo' && onBlock && (
                    <DropdownMenuItem onClick={() => onBlock(member)} className="cursor-pointer">
                      <Lock className="mr-2 h-4 w-4" />
                      {translateInterfaceText("Bloquear")}</DropdownMenuItem>
                  )}
                  {member.status === 'expirado' && onUnblock && (
                    <DropdownMenuItem onClick={() => onUnblock(member)} className="cursor-pointer">
                      <Unlock className="mr-2 h-4 w-4" />
                      {translateInterfaceText("Desbloquear")}</DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => onRemove(member)}
                    className="cursor-pointer text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {translateInterfaceText("Remover membro")}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
